// As ações de cada linha da lista de clientes: o menu e o que ele dispara. (#880)

import { useState } from 'react';
import { type ClienteDaLista } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { type PedidoDeAtendimento } from './iniciar-atendimento';

/**
 * As ações da linha, montadas uma vez para as duas peças.
 *
 * A tabela e a lista de celular mostram os MESMOS controles, e duplicá-los era
 * garantir que um dia um ganhasse um item que o outro não tem — com o defeito
 * aparecendo só no telefone.
 *
 * "Atribuir" está aqui, e passa a CONVERSA — não o cliente.
 *
 * Cliente não tem dono no modelo (§5.1, decisão de 24/08): quem tem
 * responsável é a conversa e a oportunidade. Então "passar" nesta linha
 * significa passar o que de fato tem dono, e a rota move os dois juntos.
 *
 * Sem conversa não há por onde: o item aparece desabilitado com o motivo, em
 * vez de sumir — sumir faria o menu ter tamanhos diferentes linha a linha, e
 * ninguém saberia por quê.
 */
export interface AcoesDaLinha {
  readonly abrirConversa: (cliente: ClienteDaLista) => void;
  readonly abrirFicha: (cliente: ClienteDaLista) => void;
  readonly criarTarefa: (cliente: ClienteDaLista) => void;
  readonly passar: (cliente: ClienteDaLista) => void;
  /** A conversa que o diálogo de atribuição está passando, ou nada. */
  readonly passando: string | null;
  readonly fecharPassagem: () => void;
  readonly recado: string | null;
  /*
   * Primeiro contato (C31) — o caminho de quem NUNCA escreveu.
   *
   * "Abrir conversa" fica desabilitado para essa gente, e até aqui o menu não
   * oferecia alternativa: o cliente cadastrado à mão era o único com quem não
   * havia como falar.
   */
  readonly iniciarAtendimento: (cliente: ClienteDaLista) => void;
  /** O cliente no diálogo de primeiro contato, ou nada. */
  readonly contatando: ClienteDaLista | null;
  readonly fecharContato: () => void;
  readonly enviarPrimeiroContato: (pedido: PedidoDeAtendimento) => void;
  readonly enviandoContato: boolean;
  readonly erroDoContato: string | null;
}

export function useAcoesDaLinha(
  aoAbrirCliente: (clienteId: string) => void,
  aoAbrirConversa: (conversaId: string) => void,
): AcoesDaLinha {
  const api = useApi();
  const [recado, definirRecado] = useState<string | null>(null);
  const [passando, definirPassando] = useState<string | null>(null);
  const [contatando, definirContatando] = useState<ClienteDaLista | null>(null);
  const [enviandoContato, definirEnviandoContato] = useState(false);
  const [erroDoContato, definirErroDoContato] = useState<string | null>(null);

  return {
    iniciarAtendimento: (cliente) => {
      definirErroDoContato(null);
      definirContatando(cliente);
    },
    contatando,
    fecharContato: () => {
      definirContatando(null);
      definirErroDoContato(null);
    },
    enviandoContato,
    erroDoContato,
    enviarPrimeiroContato: (pedido) => {
      const cliente = contatando;
      if (!api || !cliente) return;

      definirEnviandoContato(true);
      definirErroDoContato(null);
      void api
        .iniciarAtendimento({
          caminho: { id: cliente.id },
          corpo: {
            inbox_referencia_externa: pedido.inbox_referencia_externa,
            // O contrato quer lista mutável; a do diálogo é somente leitura.
            template: { ...pedido.template, valores: [...pedido.template.valores] },
          },
          /*
           * Chave por TENTATIVA: sem ela, o segundo clique manda o modelo duas
           * vezes, e o WhatsApp entrega as duas para o cliente.
           */
          idempotencyKey:
            globalThis.crypto?.randomUUID?.() ?? `contato-${Date.now()}-${Math.random()}`,
        })
        .then(() => {
          definirContatando(null);
          /*
           * O recado diz que a conversa VAI aparecer, e não que já apareceu.
           *
           * O canônico dela é cunhado na ingestão, quando o evento volta pelo
           * webhook — prometer "conversa aberta" e a fila não mostrar nada por
           * dois segundos seria a tela mentindo no melhor caso.
           */
          definirRecado(
            `Mensagem enviada para ${cliente.nome_exibicao ?? 'o contato'}. A conversa aparece na fila em instantes.`,
          );
        })
        .catch((erro: unknown) =>
          definirErroDoContato(
            recadoDeErro(erro, { generica: 'Não foi possível enviar. Tente de novo.' }),
          ),
        )
        .finally(() => definirEnviandoContato(false));
    },
    passar: (cliente) => {
      if (cliente.conversa_id) definirPassando(cliente.conversa_id);
    },
    passando,
    fecharPassagem: () => definirPassando(null),
    abrirConversa: (cliente) => {
      if (cliente.conversa_id) aoAbrirConversa(cliente.conversa_id);
    },
    abrirFicha: (cliente) => aoAbrirCliente(cliente.id),
    criarTarefa: (cliente) => {
      if (!api) return;
      const quem = cliente.nome_exibicao ?? 'contato sem nome';
      void api
        .criarTarefa({
          corpo: {
            titulo: `Retomar contato com ${quem}`,
            alvo: { tipo: 'cliente', id: cliente.id },
            origem: {
              tipo: 'manual',
              referencia: cliente.id,
              motivo: 'Criada na lista de clientes',
            },
          },
          idempotencyKey:
            globalThis.crypto?.randomUUID?.() ?? `tarefa-${Date.now()}-${Math.random()}`,
        })
        .then(() => definirRecado(`Tarefa criada para ${quem}.`))
        .catch(() => definirRecado('Não foi possível criar a tarefa. Tente de novo.'));
    },
    recado,
  };
}
